import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Subscribes to a window event for the lifetime of the component.
 *
 * The handler is held in a ref and refreshed every render, so the listener registers
 * once per event type instead of churning whenever the handler closes over new values.
 * That means handlers always see current props/state without being listed as effect
 * dependencies — and an event that fires between renders can no longer land on a stale
 * closure or, worse, on no listener at all during a re-registration gap.
 */
export function useWindowEvent<K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void
): void;
export function useWindowEvent(type: string, handler: (event: Event) => void): void;
export function useWindowEvent(type: string, handler: (event: Event) => void): void {
  const handlerRef = useRef(handler);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (event: Event) => handlerRef.current(event);
    window.addEventListener(type, listener);
    return () => {
      window.removeEventListener(type, listener);
    };
  }, [type]);
}
